import { Link } from "react-router-dom";
import {
  Hammer,
  Wrench,
  Search,
  Siren,
  ArrowRight,
  ShieldCheck,
  FileText,
  Clock,
  MapPin,
} from "lucide-react";
import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";
import CTABanner from "@/components/sections/CTABanner";
import { canonicalFor } from "@/lib/site";

const REPAIR_FEATURES = [
  "Same-day leak assessments",
  "Storm & wind damage",
  "Flashing, vents & valleys",
  "Shingle & metal repairs",
  "Insurance-grade documentation",
];

const INSTALL_FEATURES = [
  "Full tear-off & deck inspection",
  "Architectural asphalt, metal, cedar, composite",
  "GAF / IKO / Malarkey certified crews",
  "10-year workmanship warranty in writing",
  "Most homes completed in 1–3 days",
];

const ADDITIONAL = [
  {
    icon: Search,
    title: "Roof Inspection",
    desc: "27-point inspections with full photo report — ideal for pre-purchase, post-storm, or annual maintenance.",
  },
  {
    icon: Siren,
    title: "Emergency Services",
    desc: "24/7 tarp and stabilize crews on call across the Lower Mainland. Storm response within hours.",
  },
];

const MATERIALS = [
  { name: "Architectural Asphalt", life: "30–50 yr", brands: "GAF, IKO, Malarkey" },
  { name: "Standing-Seam Metal", life: "50+ yr", brands: "Westform, Vicwest" },
  { name: "Cedar Shake / Shingle", life: "25–40 yr", brands: "Premium #1 grade" },
  { name: "Synthetic Composite", life: "40+ yr", brands: "DaVinci, Brava" },
];

const ASSURANCES = [
  { icon: ShieldCheck, title: "Licensed & Insured", desc: "$5M liability, WCB coverage, all crews." },
  { icon: FileText, title: "Workmanship Warranty", desc: "10-year labor warranty in writing." },
  { icon: Clock, title: "On-Time Guarantee", desc: "Show up when we say. Finish when we promise." },
];

const SERVICE_AREAS = [
  "Vancouver",
  "Burnaby",
  "North Vancouver",
  "West Vancouver",
  "Richmond",
  "Surrey",
  "Coquitlam",
  "Port Moody",
  "New Westminster",
  "Delta",
  "Langley",
  "Maple Ridge",
  "Pitt Meadows",
  "Abbotsford",
  "Mission",
];

function FeatureBlock({ icon: Icon, eyebrow, heading, desc, features, href, image, imageAlt, reverse, idx }) {
  return (
    <article
      data-testid={`feature-block-${idx}`}
      className="reveal grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
    >
      {/* Image */}
      <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
        <div className="relative aspect-[4/5] overflow-hidden bg-[#0a0a0a] border border-white/10">
          <img
            src={image}
            alt={imageAlt}
            width="1400"
            height="1750"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/60 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-5 left-5 w-10 h-10 flex items-center justify-center bg-[var(--crest-amber)] text-[#121212]">
            <Icon size={18} strokeWidth={2} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-12 bg-[var(--crest-amber)]" />
          <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
            {eyebrow}
          </span>
        </div>
        <h2
          data-testid={`feature-block-heading-${idx}`}
          className="font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
          style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.5rem)" }}
        >
          {heading}
        </h2>
        <p className="font-body text-base md:text-lg text-white/65 mt-6 leading-relaxed max-w-xl">
          {desc}
        </p>
        <ul className="mt-8 space-y-3">
          {features.map((f) => (
            <li
              key={f}
              className="font-body text-sm md:text-[15px] text-white/75 flex items-center gap-3"
            >
              <span className="h-px w-6 bg-[var(--crest-amber)]" />
              {f}
            </li>
          ))}
        </ul>
        <Link
          to={href}
          data-testid={`feature-block-link-${idx}`}
          className="group inline-flex items-center gap-2 mt-10 font-display uppercase tracking-[0.3em] text-xs font-bold text-[var(--crest-amber)] border-b border-[var(--crest-amber)]/40 pb-2 hover:border-[var(--crest-amber)] transition-colors"
        >
          Learn more about this service
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </article>
  );
}

export default function ServicesPage() {
  return (
    <>
      <Seo
        title="Services | Crestline Roofing Ltd."
        description="From roof repairs to full installations, Crestline Roofing Ltd. covers Vancouver and the Fraser Valley. Reliable work, honest pricing. Request a free quote."
        canonical={canonicalFor("/services")}
      />
      <PageLayout>
        {({ openEstimate }) => (
          <main data-testid="services-page" className="bg-[var(--crest-charcoal)] text-white">
            <PageHero
              eyebrow="Our Services"
              title="Roofing Services in Vancouver & the Fraser Valley"
              subtitle="Installation, repair, inspection, and emergency response — for homes across Vancouver, the North Shore, Burnaby, Surrey, Coquitlam, Richmond, and the entire Fraser Valley."
              image="https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?crop=entropy&cs=srgb&fm=webp&q=85&w=2400"
              imageAlt="Standing-seam metal roof close-up — Crestline Roofing Ltd. Vancouver"
              breadcrumb={[{ label: "Home", href: "/" }, { label: "Services" }]}
            />

            {/* Featured services — two large feature blocks */}
            <section
              data-testid="featured-services"
              className="relative shingle-texture bg-[var(--crest-slate)] py-20 md:py-32 space-y-24 md:space-y-32"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <FeatureBlock
                  idx={0}
                  icon={Wrench}
                  eyebrow="Same-day diagnosis"
                  heading="Roof Repair."
                  desc="Active leaks, lifted shingles, storm damage, failed flashing — surgical repairs done right. We trace problems back to their source, document everything, and quote a fixed price before any work begins."
                  features={REPAIR_FEATURES}
                  href="/services/roof-repair"
                  image="https://images.unsplash.com/photo-1573876384005-dabc14a8ccbd?crop=entropy&cs=srgb&fm=webp&q=85&w=1400"
                  imageAlt="Roof repair in progress on asphalt shingles — Crestline Roofing Ltd. Vancouver"
                />
              </div>

              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <FeatureBlock
                  idx={1}
                  reverse
                  icon={Hammer}
                  eyebrow="Full system installs"
                  heading="Roof Installation."
                  desc="Complete tear-off and new roof systems engineered for BC weather. Asphalt, metal, cedar, and synthetic composite — every system installed to manufacturer specification, backed by a transferable workmanship warranty."
                  features={INSTALL_FEATURES}
                  href="/services/roof-installation"
                  image="https://images.unsplash.com/photo-1686835162294-c4cd78c7eab6?crop=entropy&cs=srgb&fm=webp&q=85&w=1400"
                  imageAlt="Completed asphalt shingle roof installation — Crestline Roofing Ltd. Vancouver"
                />
              </div>
            </section>

            {/* Additional services */}
            <section
              data-testid="additional-services"
              className="relative bg-[var(--crest-charcoal)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-12">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      Also Available
                    </span>
                  </div>
                  <h3
                    className="reveal font-display uppercase font-bold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}
                  >
                    Inspection &amp; emergency services.
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {ADDITIONAL.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <article
                        key={s.title}
                        data-testid={`additional-${i}`}
                        className="reveal card-lift bg-[#161616] border border-white/10 border-t-2 border-t-[var(--crest-amber)] p-7 md:p-9"
                        style={{ transitionDelay: `${i * 80}ms` }}
                      >
                        <Icon size={32} strokeWidth={1.5} className="text-[var(--crest-amber)] mb-5" />
                        <h3 className="font-display uppercase font-bold text-xl md:text-2xl text-white tracking-tight mb-3">
                          {s.title}
                        </h3>
                        <p className="font-body text-sm md:text-[15px] text-white/65 leading-relaxed">{s.desc}</p>
                      </article>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Materials (h3) */}
            <section
              data-testid="materials-section"
              className="relative bg-[#101822] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-14">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      Materials We Trust
                    </span>
                  </div>
                  <h3
                    className="reveal font-display uppercase font-bold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}
                  >
                    Only manufacturer-certified systems.
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {MATERIALS.map((m, i) => (
                    <div
                      key={m.name}
                      data-testid={`material-${i}`}
                      className="reveal bg-[#161616] border border-white/10 p-6 md:p-7"
                      style={{ transitionDelay: `${i * 60}ms` }}
                    >
                      <div className="font-display uppercase tracking-[0.3em] text-[10px] text-[var(--crest-amber)] mb-3">
                        {m.life} lifespan
                      </div>
                      <h4 className="font-display uppercase font-bold text-lg text-white tracking-tight leading-tight mb-3">
                        {m.name}
                      </h4>
                      <p className="font-body text-xs text-white/55 leading-relaxed">
                        {m.brands}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Assurances */}
            <section
              data-testid="assurances-section"
              className="relative bg-[var(--crest-cream)] text-[#121212] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                  {ASSURANCES.map((a, i) => {
                    const Icon = a.icon;
                    return (
                      <div key={a.title} className="reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                        <div className="w-12 h-12 flex items-center justify-center bg-[#121212] text-[var(--crest-amber)] mb-5">
                          <Icon size={22} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-display uppercase font-bold text-xl tracking-tight mb-2">
                          {a.title}
                        </h3>
                        <p className="font-body text-sm text-[#3a3a3a] leading-relaxed">{a.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Service area */}
            <section
              data-testid="services-service-area"
              className="relative shingle-texture bg-[var(--crest-slate)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                  <div className="lg:col-span-5">
                    <div className="reveal flex items-center gap-3 mb-6">
                      <span className="h-px w-12 bg-[var(--crest-amber)]" />
                      <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                        Service Area
                      </span>
                    </div>
                    <h3
                      data-testid="services-area-heading"
                      className="reveal font-display uppercase font-bold text-white leading-[0.95] tracking-tight"
                      style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.8rem)" }}
                    >
                      Vancouver, the Fraser Valley &amp; everywhere in between.
                    </h3>
                    <p className="reveal font-body text-base md:text-lg text-white/65 leading-relaxed mt-6 max-w-md">
                      Crews dispatched daily across the Lower Mainland and Fraser Valley — from the North Shore to Mission, Abbotsford, Langley, and beyond.
                    </p>
                  </div>
                  <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SERVICE_AREAS.map((c, i) => (
                      <div
                        key={c}
                        data-testid={`services-area-${i}`}
                        className="reveal flex items-center gap-3 p-4 bg-[#141d2e] border border-white/10"
                        style={{ transitionDelay: `${i * 30}ms` }}
                      >
                        <MapPin size={12} className="text-[var(--crest-amber)] flex-shrink-0" />
                        <span className="font-display uppercase tracking-[0.15em] text-xs font-semibold text-white/85">
                          {c}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <CTABanner
              onOpenEstimate={openEstimate}
              eyebrow="Ready when you are"
              heading="Request a Free Quote."
              subheading="48-hour response. No commission games. Just a clean number."
              buttonLabel="Get My Free Quote"
            />
          </main>
        )}
      </PageLayout>
    </>
  );
}
